import { Fragment } from "react";
import type { ReactNode } from "react";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import {
  DialogCloseButton,
  DiscardChangesDialog,
  useDialogCloseGuard,
} from "@/components/ui/dialogCloseGuard";
import { useModalStackLayer } from "@/lib/modalStack";
import { useDialogDismissGuard } from "@/components/ui/useDialogDismissGuard";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  /**
   * Tailwind z-index-Klasse – nur noch Fallback außerhalb des zentralen
   * Modal-Stacks (`ModalStackProvider`). Innerhalb des Stacks wird der
   * z-Index dynamisch aus der Öffnungsreihenfolge vergeben.
   */
  zIndexClass?: string;
  /**
   * Stack-Gruppe (Fenster-Verbund) für den zentralen Modal-Stack. Ohne Angabe
   * wird die per `ModalStackGroup` geerbte Gruppe verwendet.
   */
  stackGroup?: string;
  panelClassName?: string;
  bodyClassName?: string;
  /** Auf schmalen Viewports fast volle Bildschirmhöhe, ohne Außenabstand. */
  fullScreenMobile?: boolean;
  /** Schließen-Button oben rechts anzeigen (Standard: true). */
  showCloseButton?: boolean;
  /** Bei true: Klick auf Overlay, Escape oder X erfordert Bestätigung. */
  dirty?: boolean;
  /**
   * Ohne backdrop-blur – für verschachtelte Dialoge über einem bereits
   * geblurten Overlay (verhindert Hintergrund-Zucken durch doppeltes Blur).
   */
  plainBackdrop?: boolean;
  /**
   * Bei Interaktion mit dem Dialog (Pointer down) – z. B. Fenster in den
   * Vordergrund holen, wenn mehrere In-App-Dialoge parallel offen sind.
   */
  onActivate?: () => void;
}

const SIZES: Record<NonNullable<DialogProps["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
  "3xl": "max-w-6xl",
  "4xl": "max-w-7xl",
};

function nextZIndexClass(zIndexClass: string): string {
  const match = zIndexClass.match(/z-\[(\d+)\]/);
  if (match) {
    return `z-[${Number(match[1]) + 10}]`;
  }
  return "z-[60]";
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  zIndexClass = "z-50",
  stackGroup,
  panelClassName,
  bodyClassName,
  fullScreenMobile = false,
  showCloseButton = true,
  dirty = false,
  plainBackdrop = false,
  onActivate,
}: DialogProps) {
  const { requestClose, confirmOpen, confirmDiscard, cancelDiscard, DialogRequestCloseContext } =
    useDialogCloseGuard(onClose, dirty);
  const guardedClose = useDialogDismissGuard(open, requestClose);
  const { zIndex, bringToFront } = useModalStackLayer(open, stackGroup);

  const showHeader = Boolean(title || description || showCloseButton);

  return (
    <>
      <Transition show={open} as={Fragment}>
        <HuiDialog
          as="div"
          className={clsx("relative", zIndexClass)}
          style={zIndex != null ? { zIndex } : undefined}
          onClose={guardedClose}
          onPointerDownCapture={() => {
            bringToFront();
            onActivate?.();
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={clsx(
                "fixed inset-0 bg-black/50",
                !plainBackdrop && "backdrop-blur-sm",
              )}
              aria-hidden
            />
          </TransitionChild>

          <div
            className={clsx(
              "fixed inset-0",
              fullScreenMobile ? "max-sm:overflow-hidden sm:overflow-y-auto" : "overflow-y-auto",
            )}
          >
            <div
              className={clsx(
                "flex min-h-full items-center justify-center p-4",
                fullScreenMobile && "max-sm:h-full max-sm:min-h-0 max-sm:items-stretch max-sm:p-0",
              )}
            >
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2 scale-[0.99]"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-2 scale-[0.99]"
              >
                <DialogPanel
                  className={clsx(
                    "flex w-full transform flex-col overflow-hidden rounded-ios-lg bg-card shadow-ios-md transition",
                    SIZES[size],
                    fullScreenMobile &&
                      "max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-none max-sm:rounded-none max-sm:shadow-none",
                    panelClassName,
                  )}
                >
                  {showHeader && (
                    <div
                      className={clsx(
                        "eh-dialog-header flex shrink-0 items-start gap-3 border-b border-separator px-5 py-4",
                        fullScreenMobile &&
                          "max-sm:px-4 max-sm:py-3 max-sm:pt-[max(0.75rem,env(safe-area-inset-top))]",
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        {title ? (
                          <DialogTitle className="text-base font-semibold text-label">{title}</DialogTitle>
                        ) : null}
                        {description ? (
                          <p className={clsx("text-sm text-label-secondary", title && "mt-1")}>
                            {description}
                          </p>
                        ) : null}
                      </div>
                      {showCloseButton ? <DialogCloseButton onClick={requestClose} /> : null}
                    </div>
                  )}
                  <DialogRequestCloseContext.Provider value={requestClose}>
                    <div
                      className={clsx(
                        "min-h-0 flex-1 px-5 py-4",
                        fullScreenMobile &&
                          "max-sm:overflow-y-auto max-sm:px-4 max-sm:py-3 timesheet-flow-scroll",
                        bodyClassName,
                      )}
                    >
                      {children}
                    </div>
                    {footer ? (
                      <div
                        className={clsx(
                          "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-separator bg-card-elevated px-5 py-3",
                          fullScreenMobile &&
                            "max-sm:px-4 max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]",
                        )}
                      >
                        {footer}
                      </div>
                    ) : null}
                  </DialogRequestCloseContext.Provider>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </HuiDialog>
      </Transition>

      <DiscardChangesDialog
        open={confirmOpen}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        zIndexClass={nextZIndexClass(zIndexClass)}
      />
    </>
  );
}

export {
  useDialogRequestClose,
  DialogCloseButton,
  DialogCancelButton,
} from "@/components/ui/dialogCloseGuard";
